import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { FaRegStickyNote, FaTimes, FaExclamationTriangle } from 'react-icons/fa';
import Select from "react-select";
import { rootUrl } from '../../config/config';
import { useAuth } from './AuthContext';

const fmt = (v) => Number(v || 0).toLocaleString('en-KE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtKes = (v) => `KES ${fmt(v)}`;
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';
const fmtDateTime = (d) => d ? new Date(d).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true }) : '—';

const TYPE_COLOURS = {
    WKI: 'bg-green-100 text-green-700',
    WKR: 'bg-blue-100 text-blue-700',
    EXP: 'bg-red-100 text-red-700',
    ADJ: 'bg-amber-100 text-amber-700',
    WAR: 'bg-purple-100 text-purple-700',
};
const typeBadgeClass = (t) => TYPE_COLOURS[String(t || '').toUpperCase()] || 'bg-slate-100 text-slate-600';

const selectStyles = {
    control: (base, state) => ({
        ...base,
        minHeight: '34px',
        fontSize: '12px',
        borderColor: state.isFocused ? '#FF5733' : '#e2e8f0',
        boxShadow: state.isFocused ? '0 0 0 1px #FF5733' : 'none',
        '&:hover': { borderColor: '#FF5733' },
    }),
    option: (base, state) => ({
        ...base,
        fontSize: '12px',
        backgroundColor: state.isSelected ? '#FF5733' : state.isFocused ? '#FFF1EB' : '#fff',
        color: state.isSelected ? '#fff' : '#1e293b',
    }),
    menu: (base) => ({ ...base, zIndex: 30 }),
    valueContainer: (base) => ({ ...base, padding: '0 8px' }),
    indicatorsContainer: (base) => ({ ...base, height: '34px' }),
};

const DeletedTransactions = () => {
    const { token } = useAuth();
    const getHeaders = () => ({ Authorization: `Bearer ${token || localStorage.getItem('authToken')}` });

    const [transactions, setTransactions] = useState([]);
    const [cars, setCars] = useState([]);
    const [noteModal, setNoteModal] = useState(null);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);

    const [car, setCar] = useState('');
    const [dateFrom, setDateFrom] = useState('2024-01-01');
    const [dateTo, setDateTo] = useState(new Date().toISOString().split('T')[0]);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const fetchTransactions = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const params = new URLSearchParams();
            if (dateFrom) params.append('start_date', dateFrom);
            if (dateTo)   params.append('end_date', dateTo);
            if (car)      params.append('plate_no', car);

            const res = await axios.get(`${rootUrl}/cars/deleted-transactions/list?${params}`, { headers: getHeaders() });
            if (res.data.success) {
                setTransactions(res.data.data || []);
            } else {
                setError(res.data.message || 'Failed to load archived transactions');
                setTransactions([]);
            }
        } catch (e) {
            const msg = e.response?.data?.message || e.message || 'Failed to load archived transactions';
            setError(msg);
            setTransactions([]);
        } finally {
            setLoading(false);
        }
    }, [token, dateFrom, dateTo, car]);

    useEffect(() => { fetchTransactions(); }, [fetchTransactions]);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        axios.get(`${rootUrl}/cars`, { headers: getHeaders() })
            .then(r => setCars(Array.isArray(r.data) ? r.data : []))
            .catch(() => {});
    }, [token]);

    const carOptions = [{ value: '', label: 'All Cars' }, ...cars.map(c => ({ value: c.plate_no, label: c.plate_no }))];

    const totalAmount = transactions.reduce((s, t) => s + Number(t.amount || 0), 0);

    return (
        <div className="min-h-screen bg-gradient-to-br from-[#fff8f5] to-[#EDE4DA]">
            <div className="mx-auto max-w-7xl px-4 py-6 pb-10">

                {/* Header */}
                <div className="mb-5">
                    <h1 className="text-2xl font-bold text-[#2a2e30]">Archived Transactions</h1>
                    <p className="text-sm text-gray-500 mt-0.5">Deleted car-level transactions retained for audit purposes</p>
                </div>

                {/* Filters */}
                <div className="bg-white rounded-xl border border-[#E7D6CA] shadow-sm p-4 mb-4">
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                        <div>
                            <label className="block text-[11px] font-semibold text-gray-400 uppercase tracking-wide mb-1">From</label>
                            <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)}
                                className="w-full rounded-lg border border-slate-200 px-2.5 py-1.5 text-xs text-slate-700 focus:border-[#FF5733] focus:outline-none focus:ring-1 focus:ring-[#FF5733]" />
                        </div>
                        <div>
                            <label className="block text-[11px] font-semibold text-gray-400 uppercase tracking-wide mb-1">To</label>
                            <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)}
                                className="w-full rounded-lg border border-slate-200 px-2.5 py-1.5 text-xs text-slate-700 focus:border-[#FF5733] focus:outline-none focus:ring-1 focus:ring-[#FF5733]" />
                        </div>
                        <div>
                            <label className="block text-[11px] font-semibold text-gray-400 uppercase tracking-wide mb-1">Car</label>
                            <Select options={carOptions} value={carOptions.find(o => o.value === car) || null}
                                onChange={o => setCar(o?.value || '')} styles={selectStyles} placeholder="All Cars" isClearable />
                        </div>
                        <div className="flex items-end">
                            <button onClick={fetchTransactions} disabled={loading}
                                className="w-full px-4 py-1.5 rounded-lg text-sm font-semibold text-white bg-[#FF5733] hover:bg-[#e64520] disabled:bg-gray-200 disabled:text-gray-400 transition-colors">
                                {loading ? 'Loading…' : 'Apply'}
                            </button>
                        </div>
                    </div>
                </div>

                {/* Error */}
                {error && (
                    <div className="flex items-center gap-3 bg-red-50 border border-red-200 rounded-xl px-4 py-3 mb-4 text-sm text-red-700">
                        <FaExclamationTriangle className="shrink-0" />
                        <span>{error}</span>
                        <button onClick={fetchTransactions} className="ml-auto text-xs font-semibold underline hover:no-underline">Retry</button>
                    </div>
                )}

                {/* Summary cards */}
                <div className="grid grid-cols-2 gap-3 mb-4">
                    <div className="bg-white rounded-xl border border-[#E7D6CA] shadow-sm px-4 py-3">
                        <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wide mb-1">Archived Records</p>
                        <p className="text-2xl font-bold text-[#2a2e30]">{loading ? '—' : transactions.length.toLocaleString()}</p>
                    </div>
                    <div className="bg-white rounded-xl border border-red-100 shadow-sm px-4 py-3">
                        <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wide mb-1">Total Amount</p>
                        <p className="text-lg font-bold text-red-600">{loading ? '—' : fmtKes(totalAmount)}</p>
                    </div>
                </div>

                {/* Table */}
                <div className="bg-white rounded-xl border border-[#E7D6CA] shadow-sm overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="min-w-[820px] w-full text-left">
                            <thead>
                                <tr className="border-b border-[#EFE3DB] bg-[#FFF8F5]">
                                    <th className="px-4 py-2.5 text-[11px] font-semibold uppercase tracking-wide text-slate-500">Plate</th>
                                    <th className="px-4 py-2.5 text-[11px] font-semibold uppercase tracking-wide text-slate-500">Type</th>
                                    <th className="px-4 py-2.5 text-right text-[11px] font-semibold uppercase tracking-wide text-slate-500">Amount</th>
                                    <th className="px-4 py-2.5 text-[11px] font-semibold uppercase tracking-wide text-slate-500">Trans Date</th>
                                    <th className="px-4 py-2.5 text-[11px] font-semibold uppercase tracking-wide text-slate-500">Deleted At</th>
                                    <th className="px-4 py-2.5 text-[11px] font-semibold uppercase tracking-wide text-slate-500">Deleted By</th>
                                    <th className="px-4 py-2.5 text-[11px] font-semibold uppercase tracking-wide text-slate-500">Reason</th>
                                    <th className="px-4 py-2.5 text-[11px] font-semibold uppercase tracking-wide text-slate-500">Notes</th>
                                </tr>
                            </thead>
                            <tbody>
                                {loading && (
                                    <tr><td colSpan="8" className="px-4 py-10 text-center text-sm text-gray-400">
                                        <div className="flex items-center justify-center gap-2">
                                            <div className="w-4 h-4 border-2 border-[#FF5733] border-t-transparent rounded-full animate-spin" />
                                            Loading archived transactions…
                                        </div>
                                    </td></tr>
                                )}
                                {!loading && transactions.length === 0 && (
                                    <tr><td colSpan="8" className="px-4 py-10 text-center text-sm text-gray-400">No archived transactions found for the selected filters.</td></tr>
                                )}
                                {!loading && transactions.map((tx, i) => (
                                    <tr key={tx.id || i} className="border-b border-slate-50 text-xs transition-colors hover:bg-orange-50/50 hover:border-l-2 hover:border-l-[#FF5733]">
                                        <td className="px-4 py-2.5 font-mono font-bold text-[#FF5733]">{tx.plate_no}</td>
                                        <td className="px-4 py-2.5">
                                            <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-bold ${typeBadgeClass(tx.type)}`}>{tx.type}</span>
                                            {tx.short_desc && <p className="text-[10px] text-gray-400 mt-0.5 truncate max-w-[80px]">{tx.short_desc}</p>}
                                        </td>
                                        <td className="px-4 py-2.5 text-right font-mono tabular-nums font-semibold text-slate-800">{fmt(tx.amount)}</td>
                                        <td className="px-4 py-2.5 text-slate-600">{fmtDate(tx.date)}</td>
                                        <td className="px-4 py-2.5 text-slate-600 whitespace-nowrap">{fmtDateTime(tx.deleted_at)}</td>
                                        <td className="px-4 py-2.5 text-slate-600">{tx.deleted_by || '—'}</td>
                                        <td className="px-4 py-2.5 text-slate-600 max-w-[180px]">
                                            <span className="line-clamp-2 leading-tight" title={tx.delete_reason}>{tx.delete_reason || '—'}</span>
                                        </td>
                                        <td className="px-4 py-2.5">
                                            {tx.notes ? (
                                                <button onClick={() => setNoteModal(tx.notes)}
                                                    className="flex items-center gap-1 text-[#FF5733] hover:underline">
                                                    <FaRegStickyNote className="w-3 h-3 shrink-0" />
                                                    <span className="truncate max-w-[80px]">{tx.notes}</span>
                                                </button>
                                            ) : <span className="text-gray-300">—</span>}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            {/* Note modal */}
            {noteModal && (
                <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 px-4" onClick={() => setNoteModal(null)}>
                    <div className="bg-white rounded-2xl shadow-2xl max-w-sm w-full p-6" onClick={e => e.stopPropagation()}>
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="font-bold text-[#2a2e30] flex items-center gap-2">
                                <FaRegStickyNote className="text-[#FF5733]" /> Note
                            </h3>
                            <button onClick={() => setNoteModal(null)} className="text-gray-400 hover:text-gray-600 transition"><FaTimes /></button>
                        </div>
                        <p className="text-sm text-gray-700 leading-relaxed">{noteModal}</p>
                    </div>
                </div>
            )}
        </div>
    );
};

export default DeletedTransactions;
